import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import coinIcon from "@/assets/UI/coin.webp";

import ProductStatusBadge from "../../../components/marketplace/ProductStatusBadge";
import {
  MarketplaceEmptyState,
  MarketplaceLoadingState,
} from "../../../components/marketplace/MarketplaceStates";
import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import { getMarketplaceProduct } from "../../../features/marketplace/marketplaceProductApi";
import styles from "./MarketplaceProductDetailPage.module.scss";

const isViewableProduct = (product) =>
  product?.status === "active" &&
  (!product?.moderationStatus || product?.moderationStatus === "approved");

const safeString = (value) => (typeof value === "string" ? value.trim() : "");

const getProductMediaSlides = (product) => {
  const imageSlides = (product?.listingImageUrls || [])
    .map((url) => safeString(url))
    .filter(Boolean)
    .map((url, index) => ({
      id: `img-${index}-${url}`,
      type: "image",
      src: url,
    }));

  const videoSlides = (product?.listingVideoIds || [])
    .map((videoId) => safeString(videoId))
    .filter(Boolean)
    .map((videoId, index) => ({
      id: `vid-${index}-${videoId}`,
      type: "video",
      videoId,
    }));

  return [...imageSlides, ...videoSlides];
};

const capitalize = (value) => {
  if (!safeString(value)) return "—";
  return String(value)
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
};

export default function MarketplaceProductDetailPage() {
  const { clubId, productId } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMemberBlocked, setIsMemberBlocked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadProduct = async () => {
      setLoading(true);
      setIsMemberBlocked(false);
      setNotFound(false);

      try {
        const response = await getMarketplaceProduct(clubId, productId);
        if (!isMounted) return;

        const fetchedProduct = response?.product || null;
        if (!isViewableProduct(fetchedProduct)) {
          setNotFound(true);
          setProduct(null);
          return;
        }

        setProduct(fetchedProduct);
      } catch (error) {
        if (!isMounted) return;

        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setIsMemberBlocked(true);
          setProduct(null);
          return;
        }

        setNotFound(true);
        setProduct(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      isMounted = false;
    };
  }, [clubId, productId]);

  const mediaSlides = useMemo(() => getProductMediaSlides(product), [product]);

  useEffect(() => {
    setActiveSlide(0);
  }, [product?._id]);

  const authToken = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  if (loading) return <MarketplaceLoadingState message="Loading product..." />;

  if (isMemberBlocked) {
    return (
      <section className={styles.blockedCard}>
        <h2>Join club to access marketplace</h2>
        <p>
          You need an active membership to view marketplace listing details.
        </p>
      </section>
    );
  }

  if (notFound || !product) {
    return (
      <MarketplaceEmptyState message="This product is unavailable or no longer approved." />
    );
  }

  const price = Number(product?.priceNoty ?? 0);
  const activeMedia = mediaSlides[activeSlide] || null;

  const detailRows = [
    { label: "Category", value: capitalize(product?.category) },
    {
      label: "Type",
      value: capitalize(product?.serviceType || product?.digitalType || ""),
    },
    {
      label: "Updated",
      value: product?.updatedAt
        ? new Date(product.updatedAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "—",
    },
  ];

  if (product?.category === "service") {
    detailRows.push(
      { label: "Location", value: product?.location || "—" },
      { label: "Online method", value: product?.onlineMethod || "—" },
    );
  }

  if (product?.category === "digital") {
    detailRows.push(
      {
        label: "Image pack assets",
        value:
          product?.digitalImagePackKeys?.length > 0
            ? String(product.digitalImagePackKeys.length)
            : "0",
      },
      {
        label: "Video asset",
        value: product?.digitalVideoId ? "Included" : "No",
      },
      { label: "PDF file", value: product?.digitalPdfKey ? "Included" : "No" },
    );
  }

  if (product?.category === "physical") {
    detailRows.push(
      {
        label: "Delivery method",
        value: capitalize(product?.deliveryMethod || ""),
      },
      {
        label: "Delivery details",
        value: product?.deliveryDescription || "Ask seller for details",
      },
    );
  }

  const showPrev = () => {
    if (mediaSlides.length <= 1) return;
    setActiveSlide((current) =>
      current - 1 < 0 ? mediaSlides.length - 1 : current - 1,
    );
  };

  const showNext = () => {
    if (mediaSlides.length <= 1) return;
    setActiveSlide((current) => (current + 1) % mediaSlides.length);
  };

  return (
    <section className={styles.page}>
      <div className={styles.bgGlowPink} />
      <div className={styles.bgGlowBlue} />
      <div className={styles.bgGrid} />

      <Link to="../.." relative="path" className={styles.backLink}>
        ← Back to marketplace
      </Link>

      <div className={styles.layout}>
        <section className={styles.mediaPanel}>
          <div className={styles.mainMedia}>
            {activeMedia?.type === "image" ? (
              <img
                src={activeMedia.src}
                alt={product.title}
                className={styles.heroImage}
              />
            ) : activeMedia?.type === "video" ? (
              <VideoPlayer
                token={authToken}
                videoId={activeMedia.videoId}
                mode="proxy"
                controls
                muted
                autoPlay={false}
                className={styles.heroVideo}
              />
            ) : (
              <div className={styles.mediaPlaceholder}>No media uploaded</div>
            )}

            {mediaSlides.length > 1 ? (
              <>
                <button
                  type="button"
                  className={`${styles.mediaNav} ${styles.mediaNavPrev}`}
                  onClick={showPrev}
                  aria-label="Previous media"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.mediaNav} ${styles.mediaNavNext}`}
                  onClick={showNext}
                  aria-label="Next media"
                >
                  ›
                </button>
              </>
            ) : null}
          </div>

          {mediaSlides.length > 1 ? (
            <div className={styles.thumbRow}>
              {mediaSlides.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.thumb} ${index === activeSlide ? styles.thumbActive : ""}`}
                  onClick={() => setActiveSlide(index)}
                  aria-label={`View media ${index + 1}`}
                >
                  {item.type === "image" ? (
                    <img src={item.src} alt="" aria-hidden="true" />
                  ) : (
                    <span className={styles.videoThumb}>Video {index + 1}</span>
                  )}
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section className={styles.detailsPanel}>
          <div className={styles.titleRow}>
            <h1>{product.title}</h1>
            <ProductStatusBadge
              status={product.moderationStatus || product.status}
            />
          </div>

          <p className={styles.metaLine}>
            {capitalize(product.category)}
            {product.serviceType || product.digitalType
              ? ` • ${capitalize(product.serviceType || product.digitalType)}`
              : ""}
          </p>

          <div className={styles.priceCard}>
            <p className={styles.priceLabel}>Price</p>
            <p className={styles.priceValue}>
              <span>{price}</span>
              <img
                src={coinIcon}
                alt=""
                aria-hidden="true"
                className={styles.priceIcon}
              />
            </p>
            <button type="button" className={styles.buyBtn}>
              Contact seller
            </button>
          </div>

          <section className={styles.infoCard}>
            <h2>What you get</h2>
            <p className={styles.description}>
              {product.description || "No description provided."}
            </p>
          </section>

          <section className={styles.infoCard}>
            <h2>Product details</h2>
            <dl className={styles.specGrid}>
              {detailRows.map((row) => (
                <div key={row.label} className={styles.specItem}>
                  <dt>{row.label}</dt>
                  <dd>{row.value || "—"}</dd>
                </div>
              ))}
            </dl>
          </section>
        </section>
      </div>
    </section>
  );
}
